import {
  StepDetails,
  VerificationAllOfProperties,
  VerificationProperties,
  VerificationSchema
} from "types/landlord/verificationTemplate";
import { VerificationSchemaView } from "./slices/types";
import { ApplicantType } from "shared/types";
import { incomeHistoryTabs } from "./constant";

const transformJsonToArray = (json: VerificationProperties): StepDetails[] => {
  const result: StepDetails[] = [];
  const priorityKeys = ["pre_tax_income", "offer_letter_file"];

  Object.keys(json).forEach((key) => {
    if (json[key].type === "object" && json[key].properties)
      result.push(...transformJsonToArray(json[key].properties));
    else if (
      json[key].type === "array" &&
      json[key].items &&
      json[key].items.properties
    )
      result.push(...transformJsonToArray(json[key].items.properties));
    else
      result.push({
        key: key,
        type: json[key].type,
        title: json[key].title || key
      });
  });

  return result.sort((a, b) => {
    const aPriority = priorityKeys.includes(a.key) ? 1 : 0;
    const bPriority = priorityKeys.includes(b.key) ? 1 : 0;
    return aPriority - bPriority;
  });
};

export const transformIncomeVerification = (
  schemas: VerificationSchema | undefined
) => {
  if (!schemas) return [];
  const incomeSourceSchemas =
    schemas.steps[0]?.json_schema?.allOf?.[0]?.then?.properties?.income_sources
      ?.items?.dependencies?.income_source?.oneOf || [];

  const transformedSchemas = incomeSourceSchemas.map((schema) => {
    const incomeSource = (
      schema.properties as unknown as { income_source: { enum: string[] } }
    ).income_source.enum[0];
    const key = incomeSource.toLowerCase().replace(/[\s-]+/g, "_");
    const {
      verification_method: _,
      current_income: __,
      supervisor,
      ...properties
    } = schema.properties[key]?.properties || {};

    const jsonSchema = {
      title:
        schema.properties[incomeSource.toLowerCase().replace(/\s+/g, "_")]
          ?.title || "",
      description: "",
      type: "object",
      properties: Object.entries({
        ...properties,
        ...(supervisor ? { supervisor } : {})
      }).reduce((acc, [key, value]) => {
        if (key === "supervisor" && supervisor)
          Object.assign(acc, supervisor.properties);
        else acc[key] = value;
        return acc;
      }, {}),
      required: schema.required || []
    };

    return {
      name: key,
      json_schema: jsonSchema
    };
  });
  return transformedSchemas.sort((a, b) => {
    const aIndex = incomeHistoryTabs.indexOf(a.name as VerificationSchemaView);
    const bIndex = incomeHistoryTabs.indexOf(b.name as VerificationSchemaView);
    return (
      (aIndex !== -1 ? aIndex : Infinity) - (bIndex !== -1 ? bIndex : Infinity)
    );
  });
};

export const extractSchemaProperties = (
  selectedStep: number,
  combinedVerificationSchemas: unknown
): StepDetails[] => {
  const stepData = combinedVerificationSchemas?.[selectedStep]?.json_schema;

  if (stepData && stepData?.allOf) {
    let allProperties = {};
    stepData.allOf.forEach((schema: VerificationAllOfProperties) => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (schema.then && schema.then.properties)
        allProperties = { ...allProperties, ...schema.then.properties };
    });

    if (Object.keys(allProperties).length > 0) {
      let allStepDetails: StepDetails[] = [];
      Object.keys(allProperties).forEach((key) => {
        const keyProperties = allProperties[key].items?.properties;
        if (keyProperties)
          allStepDetails = [
            ...allStepDetails,
            ...transformJsonToArray(keyProperties)
          ];
      });
      return allStepDetails;
    }
  }

  if (stepData && stepData.properties) {
    let allStepDetails: StepDetails[] = [];
    Object.keys(stepData.properties).forEach((key) => {
      const keyProperties = stepData.properties?.[key].items?.properties;
      if (keyProperties)
        allStepDetails = [
          ...allStepDetails,
          ...transformJsonToArray(keyProperties)
        ];
    });
    if (allStepDetails.length > 0) return allStepDetails;

    return transformJsonToArray(stepData.properties);
  }

  return [];
};

export const tabTitle = {
  [VerificationSchemaView.ScreeningServices]: "Screening services",
  [VerificationSchemaView.PersonalDetails]: "Personal",
  [VerificationSchemaView.AdditionalQuestions]: "Additional questions",
  [VerificationSchemaView.TenantQualifications]: "Resident qualifications",
  [VerificationSchemaView.SubmissionDisclosure]: "Submission disclosure",
  [VerificationSchemaView.DecisionCriteria]: "Criteria",
  [VerificationSchemaView.LeadSource]: "Lead source",
  [VerificationSchemaView.General]: "Other Settings",
  [VerificationSchemaView.Dependents]: "Dependents",
  [VerificationSchemaView.EmergencyContacts]: "Emergency contacts",
  [VerificationSchemaView.Pets]: "Pets and assistance animals",
  [VerificationSchemaView.Vehicles]: "Vehicles",
  [VerificationSchemaView.MilitaryFirstResponderTeacher]:
    "Military, first responder...",
  [ApplicantType.Applicant]: "Applicant",
  [ApplicantType.CoSigner]: "Guarantor",
  [ApplicantType.Occupant]: "Occupant",
  [VerificationSchemaView.BasicInformation]: "Basic Information",
  [VerificationSchemaView.IdentityVerification]: "Identity Verification",
  [VerificationSchemaView.IdentityRiskSignal]: "Risk signals",
  [VerificationSchemaView.PetScreeningReport]: "General",
  [VerificationSchemaView.PetScreeningEmailPreferences]: "Email preferences",
  [VerificationSchemaView.HousingHistory]: "Housing History",
  [VerificationSchemaView.RentalVerification]: "Housing History",
  [VerificationSchemaView.CreditReport]: "Credit Report",
  [VerificationSchemaView.CriminalReport]: "Criminal Report",
  [VerificationSchemaView.EnhancedCriminalGeneral]: "General",
  [VerificationSchemaView.EnhancedCriminalSearch]: "Criminal search",
  [VerificationSchemaView.HousingCourtReport]: "Housing court report",
  [VerificationSchemaView.PremiumWarningsReport]: "Premium warnings report",
  [VerificationSchemaView.DocumentVerification]: "Document verification",
  [VerificationSchemaView.BankVerification]: "Bank verification",
  [VerificationSchemaView.PayrollVerification]: "Payroll verification",
  [VerificationSchemaView.Employment]: "Employment",
  [VerificationSchemaView.CombinedEmployment]: "Employment",
  [VerificationSchemaView.SelfEmployed]: "Self-employed",
  [VerificationSchemaView.CombinedSelfEmployed]: "Self-employed",
  [VerificationSchemaView.OfferLetter]: "Offer letter",
  [VerificationSchemaView.CombinedOfferLetter]: "Offer letter",
  [VerificationSchemaView.OtherIncome]: "Other income",
  [VerificationSchemaView.CombinedOtherIncome]: "Other income",
  [VerificationSchemaView.CombinedIncomeVerification]: "Income history",
  [VerificationSchemaView.HousingAssistance]: "Housing assistance",
  [VerificationSchemaView.CombinedHousingAssistance]: "Housing assistance",
  [VerificationSchemaView.CombinedAdditionalQuestions]: "Additional questions",
  [VerificationSchemaView.HousingHistoryRent]: "Rent",
  [VerificationSchemaView.HousingHistoryOwn]: "Own",
  [VerificationSchemaView.HousingHistoryOther]: "Other",
  [VerificationSchemaView.HousingHistoryAdditionalQuestions]:
    "Additional questions",
  [VerificationSchemaView.RentalVerificationSelfAttested]: "Self-attested",
  [VerificationSchemaView.RentalVerificationConfiguration]: "Verification",
  [VerificationSchemaView.RentalVerificationRent]: "Rent",
  [VerificationSchemaView.RentalVerificationOwn]: "Own",
  [VerificationSchemaView.RentalVerificationOther]: "Other",
  [VerificationSchemaView.RentalVerificationSelfAttestedConfiguration]:
    "General",
  [VerificationSchemaView.RentalVerificationAdditionalQuestions]:
    "Additional questions",
  [VerificationSchemaView.RentalVerificationPlan]: "Verification plan",
  [VerificationSchemaView.RentalVerificationSurvey]: "Survey",
  [VerificationSchemaView.CountyWalkIn]: "General",
  [VerificationSchemaView.CountyWalkInAutomatic]: "Automatic search",
  [VerificationSchemaView.CountyWalkInManual]: "Manual search",
  [VerificationSchemaView.AdverseAction]: "Adverse action",
  [VerificationSchemaView.DiscountCodes]: "Discount codes",
  [VerificationSchemaView.AdditionalFees]: "Additional fees",
  [VerificationSchemaView.PetPolicy]: "Pet policy",
  [VerificationSchemaView.ApplicationGroup]: "Application group",
  [VerificationSchemaView.BoomFinanceVerification]: "General",
  [VerificationSchemaView.BoomFinanceLoanDetails]: "Loan details",
  [VerificationSchemaView.BoomFinanceHomeDetails]: "Home details",
  [VerificationSchemaView.BoomFinanceBusinessState]: "Business state",
  [VerificationSchemaView.BoomFinanceIntendedUse]: "Intended use",
  [VerificationSchemaView.BoomFinanceDemographics]: "Demographics details",
  [VerificationSchemaView.BoomFinanceIncomeHistory]: "Income history",
  [VerificationSchemaView.BoomFinanceHousingHistory]: "Housing history",
  [VerificationSchemaView.BoomFinanceDeclarations]: "Declarations",
  [VerificationSchemaView.BoomFinanceConsents]: "Consents"
};

const i18nKeyToTitle = {
  "verifications.personal-details.steps.military-first-responder-teacher.title":
    "Are you in the military, a first responder, or a teacher?",
  "verifications.personal-details.steps.vehicles.make": "Make",
  "verifications.personal-details.steps.vehicles.model": "Model",

  "verifications.personal-details.steps.vehicles.year": "Year",
  "verifications.personal-details.steps.vehicles.color": "Color",
  "verifications.personal-details.steps.vehicles.license-plate-state":
    "License plate state",
  "verifications.personal-details.steps.vehicles.license-plate-number":
    "License plate number",
  "verifications.personal-details.steps.pets.type": "Type",
  "verifications.personal-details.steps.pets.breed": "Breed",
  "verifications.personal-details.steps.pets.weight": "Weight",
  "verifications.personal-details.steps.pets.name": "Name",
  "verifications.personal-details.steps.pets.age": "Age",
  "verifications.personal-details.steps.pets.photo": "Photo",
  "verifications.personal-details.steps.pets.service-animal":
    "Is service animal",
  "verifications.personal-details.steps.pets.esa":
    "Is emotional support animal",
  "verifications.personal-details.steps.emergency-contacts.email": "Email",
  "verifications.personal-details.steps.emergency-contacts.phone-number":
    "Phone",
  "verifications.personal-details.steps.emergency-contacts.relationship":
    "Relationship",
  "verifications.personal-details.steps.emergency-contacts.last-name":
    "Last name",
  "verifications.personal-details.steps.emergency-contacts.first-name":
    "First name",
  "verifications.personal-details.steps.dependents.first-name": "First name",
  "verifications.personal-details.steps.dependents.last-name": "Last name",
  "verifications.personal-details.steps.dependents.dob": "Date of birth",
  "verifications.personal-details.steps.personal-details.contact-first-name":
    "Legal first name",
  "verifications.personal-details.steps.personal-details.contact-last-name":
    "Legal last name",
  "verifications.personal-details.steps.personal-details.contact-middle-name":
    "Legal middle name",
  "verifications.personal-details.steps.personal-details.contact-email":
    "Email",
  "verifications.personal-details.steps.personal-details.contact-phone-number":
    "Phone number",
  "verifications.income.steps.income.income-sources.job-details.company-name.title":
    "Company name",
  "verifications.income.steps.income.income-sources.job-details.job-title.title":
    "Job title",
  "verifications.income.steps.income.income-sources.job-details.start-date.title":
    "Start date/end date",
  pre_tax_income: "Pre-tax income",
  "verifications.income.steps.income.income-sources.supervisor.first-name.title":
    "Supervisor first name",
  "verifications.income.steps.income.income-sources.supervisor.last-name.title":
    "Supervisor last name",
  "verifications.income.steps.income.income-sources.supervisor.supervisor-email.title":
    "Supervisor contact email",
  "verifications.income.steps.income.income-sources.supervisor.supervisor-phone.title":
    "Supervisor contact phone",
  "verifications.income.steps.income.income-sources.offer-letter.company-name.title":
    "Company name",
  "verifications.income.steps.income.income-sources.offer-letter.job-title.title":
    "Job title",
  "verifications.income.steps.income.income-sources.offer-letter.start-date.title":
    "Start date",
  "verifications.income.steps.income.income-sources.offer-letter.upload-offer-letter.title":
    "Upload offer letter",
  "verifications.income.steps.income.income-sources.other-income.income-description.title":
    "Income description",
  "verifications.income.steps.income.income-sources.other-income.paid-by.title":
    "Paid by/from",
  "verifications.income.steps.income.income-sources.other-income.start-date.title":
    "Start date/end day"
};

export const mapKeyToTitle = (key: string) => {
  const v = i18nKeyToTitle[key];
  if (v) return v;
  return key;
};

/**
 * Updates the value of the object at the end of the path
 * Addresses the issue where manually updating objects using the spread operator
 * might lead to omissions or unintended modifications of other values.
 * @param originData - object that will be updated with the new value at the end of the keys
 * @param keys - array of strings that represent the path to the value that will be updated
 * @param value - boolean value that will be set at the end of the path
 * @returns object with the new value set at the end of the path
 * */
export const buildVerificationSchemaData = <T>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  originData: Record<string, any> | undefined,
  keys: string[],
  value: T
) => {
  const [key, ...rest] = keys;
  return {
    ...originData,
    [key]: rest.length
      ? buildVerificationSchemaData(
          (originData?.[key] as Record<string, unknown>) ?? {},
          rest,
          value
        )
      : value
  };
};

/**
 * Get the value of the object at the end of the path
 * @param json - object that will be used to get the value
 * @param path - array of strings that represent the path to the value
 * @returns value of the path
 * */
export const getValueByPath = <T>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  json: Record<string, any> | undefined | null = {},
  path?: string[]
) => {
  return path?.reduce<unknown>(
    (acc, key) => (acc as Record<string, unknown>)?.[key],
    json
  ) as T;
};

/**
 * Build the path to the value of the object
 * @param json - object that will be used to build the path
 * @param path - array of strings that represent the path to the value
 * @returns object with the path to the value
 * */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const buildPath = (json: Record<string, any>, path: string[] = []) => {
  const results: Record<string, string[]> = {};
  Object.keys(json).forEach((key) => {
    if (json[key] && typeof json[key] === "object") {
      const nested = buildPath(
        json[key] as Record<string, unknown>,
        path.concat(key)
      );
      Object.keys(nested).forEach((nestedKey) => {
        results[nestedKey] = nested[nestedKey];
      });
    } else results[key] = path.concat(key);
  });

  return results;
};
